/**
 * ProdutosPage - Produtos OPME Globais
 *
 * Lista TODOS os produtos OPME cadastrados no sistema
 * Produtos da tabela Product (não ClienteProduto)
 */
import React, { useState, useEffect } from 'react';
import {
  Package,
  RefreshCw,
  AlertCircle,
  Search
} from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { MainLayout } from '../components/MainLayout';
import { getProducts, Product } from '../services/productService';

export const ProdutosPage = () => {
  const [produtos, setProdutos] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProdutos();
  }, []);

  const loadProdutos = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getProducts();
      setProdutos(data);
      console.log('✅ Produtos carregados:', data.length);
    } catch (err: any) {
      setError('Erro ao carregar produtos: ' + (err.message || 'Erro desconhecido'));
      console.error('❌ Erro ao carregar produtos:', err);
    } finally {
      setLoading(false);
    }
  };

  const produtosFiltrados = produtos.filter(p =>
    p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.fabricante && p.fabricante.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.categoria && p.categoria.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <>
        <Sidebar />
        <MainLayout>
          <div className="flex items-center justify-center h-96">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">Carregando produtos...</span>
          </div>
        </MainLayout>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <MainLayout>
        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Package className="w-7 h-7" />
                Produtos OPME
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {produtosFiltrados.length} de {produtos.length} produto(s)
              </p>
            </div>

            <button
              onClick={loadProdutos}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, código, fabricante ou categoria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>

          {/* Products Table */}
          {produtosFiltrados.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {produtos.length === 0 ? 'Nenhum produto cadastrado' : 'Nenhum produto encontrado com esse filtro'}
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-[0.90rem]">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Código
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Fabricante
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Categoria
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        GTIN
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Preço
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {produtosFiltrados.map(produto => (
                      <tr key={produto.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-3 py-2.5">
                          <div className="text-xs font-mono font-medium text-gray-900 dark:text-white">
                            {produto.codigo}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs text-gray-900 dark:text-white">
                            {produto.nome}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {produto.fabricante || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="px-2 py-0.5 text-[0.7rem] rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            {produto.categoria || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs font-mono text-gray-600 dark:text-gray-400">
                            {produto.gtin || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="text-xs font-medium text-gray-900 dark:text-white">
                            R$ {produto.preco_unitario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </MainLayout>
    </>
  );
};
